import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Alert, 
    CircularProgress,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    Divider,
    Chip,
    LinearProgress
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ErrorOutline as ErrorOutlineIcon
} from '@mui/icons-material';
import { ProgresoRecaudacionResponse, ErrorRecaudacion } from '../services/recaudacionMasivaApiService';
import { useRecaudacionContext } from '../../../../context/recaudacionContext';

interface RecaudacionMasivaProps {
    onProgresoCompletado?: (progreso: ProgresoRecaudacionResponse) => void;
}

// Componente para mostrar errores detallados
const ErroresDetallados: React.FC<{ errores: ErrorRecaudacion[] }> = ({ errores }) => {
    const [expanded, setExpanded] = useState(false);

    if (!errores || errores.length === 0) {
        return null;
    }

    return (
        <Card sx={{ mt: 2, border: '1px solid', borderColor: 'grey.300' }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <ErrorOutlineIcon color="action" />
                    <Typography variant="h6" color="text.primary">
                        Errores Detallados ({errores.length})
                    </Typography>
                </Box>

                <Accordion 
                    expanded={expanded} 
                    onChange={() => setExpanded(!expanded)}
                    sx={{ 
                        '&:before': { display: 'none' },
                        boxShadow: 'none',
                        border: '1px solid',
                        borderColor: 'grey.200'
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2" color="text.primary">
                            Ver detalles de errores
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <List dense>
                            {errores.map((error, index) => (
                                <React.Fragment key={index}>
                                    <ListItem>
                                        <ListItemText
                                            primary={
                                                <Box>
                                                    <Typography variant="body2" fontWeight="bold" color="text.primary">
                                                        {error.descripcionError}
                                                    </Typography>
                                                    
                                                    <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                                                        <Chip 
                                                            label={`Campo: ${error.campo}`} 
                                                            size="small" 
                                                            variant="outlined"
                                                            color="primary"
                                                        />
                                                        <Chip 
                                                            label={`Línea: ${error.numeroLinea}`} 
                                                            size="small" 
                                                            variant="outlined"
                                                            color="secondary"
                                                        />
                                                        <Chip 
                                                            label={`Tipo: ${error.tipoRegistro}`} 
                                                            size="small" 
                                                            variant="outlined"
                                                            color="info"
                                                        />
                                                        {error.clave && (
                                                            <Chip 
                                                                label={`Clave: ${error.clave}`} 
                                                                size="small" 
                                                                variant="outlined"
                                                                color="warning"
                                                            />
                                                        )}
                                                        {error.id && (
                                                            <Chip 
                                                                label={`ID: ${error.id}`} 
                                                                size="small" 
                                                                variant="outlined"
                                                                color="success"
                                                            />
                                                        )}
                                                    </Box>
                                                    
                                                    <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            <strong>Campo:</strong> {error.campo} | 
                                                            <strong> Tipo de Registro:</strong> {error.tipoRegistro} | 
                                                            <strong> Línea:</strong> {error.numeroLinea}
                                                            {error.clave && ` | Clave: ${error.clave}`}
                                                            {error.id && ` | ID: ${error.id}`}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                    {index < errores.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    </AccordionDetails>
                </Accordion>
            </CardContent>
        </Card>
    );
};

const RecaudacionMasiva: React.FC<RecaudacionMasivaProps> = ({ 
    onProgresoCompletado 
}) => {
    // Usar el contexto global para obtener el estado de recaudación
    const {
        recaudacionId,
        recaudacionCompletada,
        progresoActual
    } = useRecaudacionContext();

    const [tiempoTranscurrido, setTiempoTranscurrido] = useState<string>('');

    // Actualizar el tiempo transcurrido cada segundo de forma local
    useEffect(() => {
        if (!progresoActual || recaudacionCompletada) {
            setTiempoTranscurrido('');
            return;
        }

        // Preferir fechaInicio del progreso si está disponible, si no usar ahora
        const inicio = (progresoActual as any)?.fechaInicio
            ?? (progresoActual as any)?.FechaInicio
            ?? new Date().toISOString();
        const fechaInicio = new Date(inicio);

        const formatear = () => {
            const diffMs = Date.now() - fechaInicio.getTime();
            const totalSeg = Math.max(0, Math.floor(diffMs / 1000));
            const horas = Math.floor(totalSeg / 3600);
            const minutos = Math.floor((totalSeg % 3600) / 60);
            const segundos = totalSeg % 60;
            if (horas > 0) {
                setTiempoTranscurrido(`${horas}h ${minutos.toString().padStart(2, '0')}m ${segundos.toString().padStart(2, '0')}s`);
            } else {
                setTiempoTranscurrido(`${minutos.toString().padStart(2, '0')}m ${segundos.toString().padStart(2, '0')}s`);
            }
        };

        formatear();
        const intervalId = setInterval(formatear, 1000);
        return () => clearInterval(intervalId);
    }, [progresoActual?.estado, recaudacionCompletada]);

    // Notificar cuando el proceso se complete
    useEffect(() => {
        if (recaudacionCompletada && onProgresoCompletado) {
            onProgresoCompletado(recaudacionCompletada);
        }
    }, [recaudacionCompletada, onProgresoCompletado]);

    // Si no hay recaudacionId NI proceso completado, mostrar mensaje informativo
    if (!recaudacionId && !recaudacionCompletada) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h5" component="h1" gutterBottom>
                    Resultados de Recaudación Masiva
                </Typography>
                
                <Alert severity="info" sx={{ mt: 2, maxWidth: 600, mx: 'auto' }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        📋 No hay recaudación en proceso
                    </Typography>
                    <Typography variant="body2">
                        Para ver el progreso de una recaudación, primero debe iniciar el proceso de recaudación masiva 
                        desde la pestaña "Cargados" seleccionando las filas que desea procesar.
                    </Typography>
                </Alert>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h5" component="h1" gutterBottom>
                Resultados de Recaudación Masiva
            </Typography>
           
            {/* Mostrar componente de progreso solo si el proceso no está completado */}
            {!recaudacionCompletada && progresoActual && (
                <Box sx={{ mt: 3 }}>
                    <Card sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Progreso de Recaudación
                        </Typography>
                        
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Estado: {progresoActual.estado || 'En proceso'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Procesados: {progresoActual.dividendosProcesados || 0} / {progresoActual.totalDividendos || 0}
                            </Typography>
                            {tiempoTranscurrido && (
                                <Typography variant="body2" color="text.secondary">
                                    Tiempo transcurrido: {tiempoTranscurrido}
                                </Typography>
                            )}
                        </Box>
                        
                        <LinearProgress 
                            variant="determinate" 
                            value={progresoActual.totalDividendos ? 
                                (progresoActual.dividendosProcesados / progresoActual.totalDividendos) * 100 : 0
                            } 
                            sx={{ height: 10, borderRadius: 5 }}
                        />
                    </Card>
                </Box>
            )}

            {/* Mostrar resumen final cuando el proceso esté completado */}
            {recaudacionCompletada && (
                <Box sx={{ mt: 3 }}>
                    <Alert 
                        severity={recaudacionCompletada.errores > 0 ? "warning" : "success"} 
                        sx={{ mb: 2 }}
                    >
                        <Typography variant="subtitle1">
                            {recaudacionCompletada.errores > 0 
                                ? "⚠️ Recaudación completada con errores" 
                                : "✅ Recaudación completada exitosamente"
                            }
                        </Typography>
                        <Typography variant="body2">
                            Procesamiento finalizado. Exitosos: {recaudacionCompletada.dividendosExitosos}, 
                            Rechazados: {recaudacionCompletada.dividendosRechazados}, 
                            Errores: {recaudacionCompletada.errores}
                        </Typography>
                        {recaudacionCompletada.errores > 0 && (
                            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                                📋 Se encontraron {recaudacionCompletada.errores} error(es). 
                                Revise los detalles más abajo para más información.
                            </Typography>
                        )}
                    </Alert>

                    {/* Mostrar errores detallados después de las estadísticas finales */}
                    {recaudacionCompletada.erroresDetallados && recaudacionCompletada.erroresDetallados.length > 0 && (
                        <ErroresDetallados errores={recaudacionCompletada.erroresDetallados} />
                    )}
                </Box>
            )}
        </Box>
    );
};

export default RecaudacionMasiva;